import { useCallback, useMemo, useState } from 'react'
import { Music, Check, X, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchPendingSongs, moderateSong, type SongRow } from './mediaApi'

/**
 * MUSIC.02 — MP3 moderatsiya (tz/mp3-tv). Foydalanuvchi yuklamalari (status=pending)
 * → tasdiqlash/rad. Auto-approve o'chirilганда bu navbat to'ladi.
 */
export function MusicModeration() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)

  const decide = useCallback(
    async (id: number, decision: 'approve' | 'reject') => {
      try {
        await moderateSong(id, decision)
        toast(decision === 'approve' ? 'Одобрено' : 'Отклонено', { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast('Ошибка', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )

  const columns = useMemo<AdminColumn<SongRow>[]>(
    () => [
      {
        key: 'title',
        header: 'Трек',
        type: 'text',
        minWidth: 220,
        render: (r) => (
          <span className="flex items-center gap-2">
            <Music size={14} className="text-tertiary" />
            <span className="font-medium text-primary">{r.title}</span>
          </span>
        ),
      },
      { key: 'artist', header: 'Исполнитель', type: 'text', width: 180 },
      { key: 'uploaded_by', header: 'Загрузил', type: 'number', width: 110, align: 'right' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<SongRow>[]>(
    () => [
      { id: 'approve', label: 'Одобрить', icon: <Check size={13} />, onSelect: (r) => void decide(r.id, 'approve') },
      { id: 'reject', label: 'Отклонить', icon: <X size={13} />, danger: true, onSelect: (r) => void decide(r.id, 'reject') },
    ],
    [decide],
  )

  return (
    <>
      <PageHeader title="Музыка — модерация" screenId="MUSIC.02" />
      <DataTable<SongRow>
        key={reloadKey}
        screenId="MUSIC.02"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchPendingSongs}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <ShieldCheck size={22} />, title: 'Очередь пуста' }}
      />
    </>
  )
}
